import styled from "@emotion/styled";
import { useForm } from "react-hook-form";
import {
  Stack,
  Label,
  RequiredText,
  Input,
  FlexRow,
  FakeLabel,
  Text,
  AllCaps,
  FieldError,
  RichTextEditor,
} from "atoms";
import { GenericImage } from "features/images";
import { fieldOptions } from "utils/form";
import { slugify } from "utils/text";
import {
  useUpdateProductDetails,
  type UpdateProductDetailsReq,
} from "../../api/useUpdateProductDetails";
import type { StepProps } from "../../types";
import { ProductTag } from "../ProductTag";
import { ProductWizardStep } from "../ProductWizardStep";

function StepDetails({ dispatch, state, moveBack }: StepProps) {
  const { id, title, descriptionHtml } = state.product;
  const { isLoading, mutate } = useUpdateProductDetails(id);

  const {
    control,
    register,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm<UpdateProductDetailsReq>({
    defaultValues: {
      title,
      descriptionHtml,
      mediaIDsToDelete: [],
      mediaIDsToSequence: [],
      mediaURLsToAdd: [],
    },
  });

  const moveNext = () => dispatch({ type: "moveNext" });
  const onSubmit = handleSubmit((formData) => {
    if (isDirty) {
      mutate(formData, {
        onSuccess: moveNext,
      });
    } else {
      moveNext();
    }
  });

  // Prevents duplicate tags (e.g. digital downloads)
  const showTemplateName =
    slugify(state.product.productType) !== slugify(state.productTemplate.name);
  const showAssetUrl =
    state.productTemplate.templateCategory === "Digital Download";

  return (
    <ProductWizardStep
      stepTitle="Product Details"
      state={state}
      moveBack={moveBack}
      moveBackDisabled={isDirty}
      nextStep={onSubmit}
      nextStepDisabled={isLoading}
      isForm
    >
      <Stack gap="md">
        <DetailColumns>
          {state.product.featuredImage?.url && (
            <DetailImage>
              <GenericImage
                src={state.product.featuredImage.url}
                style={{ border: "1px solid rgb(0 0 0 / .1)" }}
                sizes="300px"
              />
            </DetailImage>
          )}

          <DetailForm>
            <Stack gap="md">
              <Label>
                Product Title
                <RequiredText />
                <Input {...register("title", fieldOptions.required)} />
                {errors.title && (
                  <FieldError>{errors.title.message}</FieldError>
                )}
              </Label>

              <Label>
                Product Description
                <RequiredText />
                <RichTextEditor
                  control={control}
                  name="descriptionHtml"
                  required
                />
                {errors.descriptionHtml && (
                  <FieldError>{errors.descriptionHtml.message}</FieldError>
                )}
              </Label>

              <Stack>
                <Stack gap="0">
                  <FakeLabel>Product Category</FakeLabel>

                  <Text size="xxs">
                    <em>
                      These are set by <AllCaps>Hug</AllCaps> based on the
                      product being created.
                    </em>
                  </Text>
                </Stack>

                <FlexRow gap="10px">
                  <ProductTag type={state.product.productType} size="sm" />

                  {showTemplateName && (
                    <ProductTag type={state.productTemplate.name} size="sm" />
                  )}
                </FlexRow>
              </Stack>

              {showAssetUrl && (
                <Label>
                  Downloadable Asset
                  <Input readOnly value={state.product.assetUrl} />
                </Label>
              )}
            </Stack>
          </DetailForm>
        </DetailColumns>
      </Stack>
    </ProductWizardStep>
  );
}

StepDetails.displayName = "Details";

const DetailColumns = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  "@media (min-width: 720px)": {
    flexDirection: "row-reverse",
  },
});

const DetailImage = styled.div(({ theme }) => ({
  maxWidth: 300,
  "@media (min-width: 720px)": {
    flex: "0 0 30%",
    maxWidth: "none",
  },
  [theme.breakpoints.desktopLarge]: {
    flex: "0 0 300px",
  },
}));

const DetailForm = styled.div({
  flex: "1 0 0%",
  maxWidth: 660,
  marginRight: "auto",
});

export { StepDetails };
